import { msg } from "./messages";

let loginVisible = false;
let loginPromiseResolve: (() => void) | null = null;
let onReauthenticated: (() => void) | null = null;

export interface AuthUser {
    id: number;
    username: string;
}

let currentUser: AuthUser | null = null;

export function getCurrentUser(): AuthUser | null {
    return currentUser;
}

export function setAuthRecoveryHandler(handler: () => void): void {
    onReauthenticated = handler;
}

export function handleAuthenticationRequired(): void {
    if (loginVisible) return;
    showAuthScreen("login");
}

export async function ensureAuthenticated(): Promise<void> {
    const res = await fetch("/api/auth/status", { credentials: "include", cache: "no-store" });
    if (!res.ok) return;

    const body = (await res.json()) as { authenticated: boolean; user?: AuthUser | null };
    if (body.authenticated) {
        currentUser = body.user ?? null;
        return;
    }

    await new Promise<void>((resolve) => {
        loginPromiseResolve = resolve;
        showAuthScreen("login");
    });
}

export async function logout(): Promise<void> {
    try {
        await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
    } finally {
        currentUser = null;
        location.reload();
    }
}

function showAuthScreen(mode: "login" | "register"): void {
    loginVisible = true;
    document.getElementById("bottom-nav")?.classList.add("hidden");

    const isRegister = mode === "register";
    const main = document.getElementById("app-main")!;
    main.innerHTML = `
        <section class="auth-login" aria-labelledby="auth-login-title">
            <div class="auth-login-card">
                <h1 id="auth-login-title" class="auth-login-title">${escapeHtml(isRegister ? msg("auth/register-title") : msg("auth/login-title"))}</h1>
                <p class="auth-login-hint">${escapeHtml(isRegister ? msg("auth/register-hint") : msg("auth/login-hint"))}</p>
                <form id="auth-login-form" class="auth-login-form">
                    <div class="form-group">
                        <label for="auth-username">${escapeHtml(msg("auth/username"))}</label>
                        <input
                            id="auth-username"
                            class="form-input"
                            type="text"
                            name="username"
                            autocomplete="username"
                            required
                        />
                    </div>
                    <div class="form-group">
                        <label for="auth-password">${escapeHtml(msg("auth/login-password"))}</label>
                        <input
                            id="auth-password"
                            class="form-input"
                            type="password"
                            name="password"
                            autocomplete="${isRegister ? "new-password" : "current-password"}"
                            required
                        />
                    </div>
                    <p id="auth-login-error" class="auth-login-error hidden" role="alert"></p>
                    <button type="submit" class="btn btn-primary btn-block btn-lg">${escapeHtml(isRegister ? msg("auth/register-submit") : msg("auth/login-submit"))}</button>
                    <button type="button" id="auth-switch-mode" class="btn btn-block">${escapeHtml(isRegister ? msg("auth/switch-to-login") : msg("auth/switch-to-register"))}</button>
                </form>
            </div>
        </section>
    `;

    const form = document.getElementById("auth-login-form") as HTMLFormElement;
    const usernameInput = document.getElementById("auth-username") as HTMLInputElement;
    const passwordInput = document.getElementById("auth-password") as HTMLInputElement;
    const errorEl = document.getElementById("auth-login-error")!;

    document.getElementById("auth-switch-mode")!.addEventListener("click", () => {
        showAuthScreen(isRegister ? "login" : "register");
    });

    form.addEventListener("submit", (e) => {
        e.preventDefault();
        void submitAuth(
            mode,
            usernameInput.value.trim(),
            passwordInput.value,
            errorEl,
            form.querySelector("button") as HTMLButtonElement,
        );
    });

    usernameInput.focus();
}

async function submitAuth(
    mode: "login" | "register",
    username: string,
    password: string,
    errorEl: HTMLElement,
    submitBtn: HTMLButtonElement,
): Promise<void> {
    errorEl.classList.add("hidden");
    submitBtn.disabled = true;

    try {
        const res = await fetch(`/api/auth/${mode === "register" ? "register" : "login"}`, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ username, password }),
        });

        if (!res.ok) {
            let message = msg("auth/login-failed");
            try {
                const data = (await res.json()) as { error?: string };
                if (data.error === "username_taken") message = msg("auth/username-taken");
                else if (data.error && res.status === 400) message = data.error;
            } catch {
                /* keep default message */
            }
            errorEl.textContent = message;
            errorEl.classList.remove("hidden");
            return;
        }

        const body = (await res.json()) as { user?: AuthUser };
        currentUser = body.user ?? null;

        hideLoginScreen();
        if (onReauthenticated) onReauthenticated();
        else if (loginPromiseResolve) {
            loginPromiseResolve();
            loginPromiseResolve = null;
        }
    } catch {
        errorEl.textContent = msg("auth/login-failed");
        errorEl.classList.remove("hidden");
    } finally {
        submitBtn.disabled = false;
    }
}

function hideLoginScreen(): void {
    loginVisible = false;
    document.getElementById("bottom-nav")?.classList.remove("hidden");
    document.getElementById("app-main")!.innerHTML = "";
}

function escapeHtml(text: string): string {
    return text
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;");
}
